// Neu (Rückmeldung 01.10.: „fix search bar to also search for tools and functions and pages and ai
// asking“) — das Verzeichnis der Befehlssuche: jede Seite und jedes Werkzeug der Anwendung mit
// deutschem Titel und Stichwörtern, gefiltert nach den Rechten der Person und danach, ob ein
// Verfahren gewählt ist. Dazu das Abgleichen (Groß/klein, Umlaute, Anfang vor Teilwort) und die
// Zeilen unter dem Suchfeld: Seiten & Werkzeuge · Codes & Text · KI fragen.
//
// Die Wege stammen aus der Wegtabelle in App.tsx; die Reiter tragen dieselben Texte wie auf ihren
// Bildschirmen (TX.pruefen.reiter*, TX.orte.reiter, TX.admin.reiter). Rein und ohne React — der
// Baustein steht in Befehlssuche.tsx. E32: „Karte“ und „Beiträge auf der Karte“ (?zeige=beitraege).
import type { Ich, Recht } from "../../api/typen";
import type { IconName } from "../ui/Icon";
import { hatRecht, istAdmin } from "../../lib/rechte";
import { baueAuswertung, leseAuswertung } from "../../lib/rezept";
import { TX } from "../../lib/texte";

/** Handlungen, die keine Adresse sind — die Befehlssuche führt sie aus. */
export type BefehlAktion = "thema" | "tastenkuerzel" | "abmelden" | "auftraege" | "ki" | "rohdaten";

export interface Befehl {
  id: string;
  titel: string;
  /** Wo es liegt („Prüfen“, „Verwaltung“) — steht rechts in der Zeile und wird mitgesucht. */
  ort: string;
  icon: IconName;
  stichworte: readonly string[];
  /** Adresse … */
  zu?: string;
  /** … oder eine Handlung der Schale. */
  aktion?: BefehlAktion;
  /** Verfahren wechseln (setzt `hs.verfahren` und führt zur Auswertung). */
  verfahren?: string;
}

interface Vorlage {
  id: string;
  titel: string;
  ort: string;
  icon: IconName;
  stichworte: readonly string[];
  /** Weg unter /v/{V} — der Eintrag braucht dann ein gewähltes Verfahren. */
  pfad?: string;
  /** fester Weg ohne Verfahren */
  zu?: string;
  aktion?: BefehlAktion;
  /** Recht im gewählten Verfahren (Vorgabe für `pfad`: lesen). */
  recht?: Recht;
  /** nur Rolle admin (/admin/*) */
  admin?: true;
}

const O = TX.befehle.ort;
const PR = TX.pruefen;

/** Das Verzeichnis. Reihenfolge = Anzeige ohne Suchwort (und Gleichstand beim Abgleich). */
const VORLAGEN: readonly Vorlage[] = [
  // ---- Auswertung (Reiter über ?tab=, lib/rezept.ts)
  { id: "auswertung", titel: TX.shell.auswertung, ort: O.seite, icon: "chart-bar", pfad: "/auswerten", stichworte: ["Dashboard", "Diagramme", "Kacheln", "Kennzahlen", "Zahlen", "Statistik", "Analyse", "Überblick", "Filter", "Fragebar", "Start"] },
  { id: "auswertung-matrix", titel: "Themen × Merkmal", ort: O.auswertung, icon: "grid-four", pfad: "/auswerten?tab=matrix", stichworte: ["Matrix", "Kreuztabelle", "Themenmatrix", "Altersgruppe"] },
  { id: "auswertung-antworten", titel: "Antworten", ort: O.auswertung, icon: "rows", pfad: "/auswerten?tab=antworten", stichworte: ["Antwortliste", "Liste", "Tabelle", "alle Antworten", "Wortlaut", "Lesefassung"] },
  { id: "auswertung-zitate", titel: "Zitatwand", ort: O.auswertung, icon: "quotes", pfad: "/auswerten?tab=zitate", stichworte: ["Zitate", "Aussagen", "O-Töne", "Stimmen"] },
  // ---- Karte (E32, screens/karte): Grundkarte, Stadtteile, Orte, Beiträge
  { id: "karte", titel: TX.karte.titel, ort: O.seite, icon: "map-trifold", pfad: "/karte", stichworte: ["Map", "Landkarte", "Stadtplan", "MapLibre", "Beiträge auf der Karte", "Ebenen", "Stadtteile", "Spielplätze", "räumlich"] },
  { id: "karte-beitraege", titel: TX.befehle.karteBeitraege, ort: O.karte, icon: "map-pin", pfad: "/karte?zeige=beitraege", stichworte: ["Beiträge anschalten", "Antworten auf der Karte", "Punkte", "Orte der Kinder", "Cluster"] },
  // ---- Prüfen mit seinen Reitern (PruefenRahmen.tsx). „Prüfen“ selbst führt wie die Hauptnavigation
  //      ohne das Recht `pruefen` auf den Lauf (navigation.ts `navZiele`).
  { id: "pruefen", titel: TX.shell.pruefen, ort: O.seite, icon: "check-square-offset", pfad: "/pruefen", stichworte: ["Korrektur", "kontrollieren", "Prüfstand", "Qualität"] },
  { id: "pruefen-warteschlange", titel: PR.reiterWarteschlange, ort: O.pruefen, icon: "list-checks", pfad: "/pruefen", recht: "pruefen", stichworte: ["Prüfen", "Korrektur", "Korrekturlesen", "kontrollieren", "Lesungen", "Transkription"] },
  { id: "pruefen-boegen", titel: PR.reiterBoegen, ort: O.pruefen, icon: "stack", pfad: "/pruefen/boegen", stichworte: ["Bögen", "Fragebögen", "Seiten zuordnen", "zusammenfassen", "aufteilen"] },
  { id: "pruefen-fragen", titel: PR.reiterFragen, ort: O.pruefen, icon: "rows", pfad: "/pruefen/fragen", recht: "pruefen", stichworte: ["Fragen-Abgleich", "Fragen", "Dubletten", "doppelte Fragen", "Formular"] },
  { id: "pruefen-bogenvorlage", titel: PR.reiterBogenvorlage, ort: O.pruefen, icon: "clipboard-text", pfad: "/pruefen/bogenvorlage", recht: "pruefen", stichworte: ["leerer Bogen", "Formular", "Formularvorlage", "Kästchen", "gedruckte Fragen", "festschreiben"] },
  { id: "pruefen-zeichnungen", titel: PR.reiterZeichnungen, ort: O.pruefen, icon: "image", pfad: "/pruefen/zeichnungen", recht: "pruefen", stichworte: ["Zeichnungen", "Karte gezeichnet", "gemalte Karte", "Plan", "Bild", "Seitenanalyse", "übersehen"] },
  { id: "pruefen-orte", titel: PR.reiterOrte, ort: O.pruefen, icon: "map-pin", pfad: "/pruefen/orte", recht: "pruefen", stichworte: ["Verortung", "Ortsvorschläge", "Stadtteil zuordnen", "Wohnort", "Orte zuordnen"] },
  { id: "pruefen-gold", titel: PR.reiterGold, ort: O.pruefen, icon: "star", pfad: "/pruefen/gold", recht: "pruefen", stichworte: ["Goldstandard", "Referenz", "Qualität", "Genauigkeit", "Stichprobe"] },
  { id: "eingang", titel: PR.reiterEingang, ort: O.pruefen, icon: "tray-arrow-up", pfad: "/eingang", recht: "hochladen", stichworte: ["Hochladen", "Upload", "Dokumente", "Chargen", "PDF", "Scans", "Import", "Masken", "Schwärzen", "Namensfelder"] },
  { id: "lauf", titel: PR.reiterLauf, ort: O.pruefen, icon: "cpu", pfad: "/lauf", stichworte: ["Verarbeitung", "OCR", "Lesen", "GPU", "Fortschritt", "Pipeline", "Modell"] },
  { id: "lauf-starten", titel: TX.befehle.laufStarten, ort: O.werkzeug, icon: "play", pfad: "/lauf", recht: "lauf", stichworte: ["starten", "OCR starten", "lesen lassen", "verarbeiten", "GPU", "Lauf"] },
  // ---- Themen
  { id: "themen", titel: TX.shell.themen, ort: O.seite, icon: "tag", pfad: "/themen", stichworte: ["Themenkatalog", "Kategorien", "Codebuch", "Katalog", "Oberthemen"] },
  { id: "kodieren", titel: TX.befehle.kodieren, ort: O.themen, icon: "pencil-simple", pfad: "/themen/kodieren", recht: "kodieren", stichworte: ["Themen zuordnen", "Kodierung", "Nennungen", "codieren"] },
  // ---- Exporte
  { id: "exporte", titel: TX.shell.exporte, ort: O.seite, icon: "export", pfad: "/exporte", stichworte: ["Excel", "CSV", "Download", "herunterladen", "Bericht", "ZIP", "PDF"] },
  { id: "rohdaten", titel: TX.befehle.rohdaten, ort: O.werkzeug, icon: "file-xls", aktion: "rohdaten", recht: "rohdaten", stichworte: ["Excel", "Export erstellen", "alle Daten", "Tabelle", "herunterladen"] },
  // ---- Ortsverzeichnis (screens/orte, Reiter über ?reiter=)
  { id: "orte", titel: TX.orte.titel, ort: O.seite, icon: "map-trifold", pfad: "/orte", stichworte: ["Orte", "Karte", "Übersicht", "Stadtteile", "Gazetteer", "Verzeichnis"] },
  { id: "orte-orte", titel: TX.orte.reiter.orte, ort: O.orte, icon: "map-pin", pfad: "/orte?reiter=orte", stichworte: ["benannte Orte", "Plätze", "Spielplätze", "Straßen", "Aliase"] },
  { id: "orte-stadtteile", titel: TX.orte.reiter.stadtteile, ort: O.orte, icon: "stack", pfad: "/orte?reiter=stadtteile", stichworte: ["Ortsbezirke", "Bezirke", "Ortsteile", "Viertel"] },
  { id: "orte-schulen", titel: TX.orte.reiter.schulen, ort: O.orte, icon: "notebook", pfad: "/orte?reiter=schulen", stichworte: ["Schulorte", "Schule verknüpfen"] },
  { id: "orte-laden", titel: TX.befehle.orteLaden, ort: O.orte, icon: "upload-simple", pfad: "/orte?reiter=laden", recht: "orte", stichworte: ["Orte laden", "OpenStreetMap", "OSM", "importieren", "Datei", "Datenbank", "GeoJSON"] },
  // ---- Werkzeuge der Schale
  { id: "auftraege", titel: TX.auftraege.titel, ort: O.werkzeug, icon: "list-checks", aktion: "auftraege", recht: "lesen", stichworte: ["Jobs", "Hintergrund", "laufende Arbeiten", "Fortschritt", "Status", "Fehler"] },
  { id: "ki", titel: TX.befehle.kiOeffnen, ort: O.werkzeug, icon: "sparkle", aktion: "ki", recht: "assistent", stichworte: ["Chat", "KI", "Assistent", "fragen", "AI"] },
  // ---- ohne Verfahren
  { id: "verfahren-alle", titel: TX.befehle.alleVerfahren, ort: O.verfahren, icon: "stack", zu: "/verfahren", stichworte: ["Übersicht", "Verfahren wechseln", "Projekte", "Kommunen"] },
  // ---- Verwaltung (Rolle admin, Admin.tsx)
  { id: "admin-nutzer", titel: TX.admin.reiter.nutzer, ort: O.verwaltung, icon: "users", zu: "/admin/nutzer", admin: true, stichworte: ["Benutzer", "Konten", "Personen", "Rollen", "entsperren"] },
  { id: "admin-zugriff", titel: TX.admin.reiter.zugriff, ort: O.verwaltung, icon: "key", zu: "/admin/zugriff", admin: true, stichworte: ["Rechte", "Berechtigungen", "Freigabe"] },
  { id: "admin-verfahren", titel: TX.befehle.verfahrenVerwalten, ort: O.verwaltung, icon: "stack", zu: "/admin/verfahren", admin: true, stichworte: ["Verfahren anlegen", "neu", "erstellen", "AVV", "Löschfrist", "Freigabe"] },
  { id: "admin-datenquellen", titel: TX.admin.reiter.datenquellen, ort: O.verwaltung, icon: "database", zu: "/admin/datenquellen", admin: true, stichworte: ["PostGIS", "Datenbank", "Verbindung"] },
  { id: "admin-protokoll", titel: TX.admin.reiter.protokoll, ort: O.verwaltung, icon: "clipboard-text", zu: "/admin/protokoll", admin: true, stichworte: ["Audit", "Log", "Verlauf"] },
  { id: "admin-system", titel: TX.admin.reiter.system, ort: O.verwaltung, icon: "gauge", zu: "/admin/system", admin: true, stichworte: ["Status", "Gesundheit", "GPU", "Platte", "Version"] },
  // ---- Konto und Anzeige
  { id: "thema", titel: "", ort: O.konto, icon: "moon", aktion: "thema", stichworte: ["Thema", "hell", "dunkel", "Dark Mode", "Light Mode", "Farbe", "Nachtmodus"] },
  { id: "tastenkuerzel", titel: TX.shell.tastenkuerzel, ort: O.konto, icon: "key", aktion: "tastenkuerzel", stichworte: ["Shortcuts", "Tastatur", "Hilfe", "Kürzel"] },
  { id: "abmelden", titel: TX.shell.abmelden, ort: O.konto, icon: "sign-out", aktion: "abmelden", stichworte: ["Logout", "ausloggen", "Konto"] },
];

export interface BefehlKontext {
  V: string | null;
  ich: Ich | undefined;
  /** gerade dunkles Thema → der Eintrag heißt „Helles Thema“ */
  dunkel: boolean;
}

/**
 * Alle Einträge, die diese Person JETZT benutzen kann: Wege mit Verfahren nur, wenn eines gewählt
 * ist und das Recht dafür da ist (der Server bleibt der Durchsetzer); die Verwaltung nur für admin;
 * dazu je zugänglichem Verfahren ein Eintrag „wechseln“.
 */
export function befehleFuer({ V, ich, dunkel }: BefehlKontext): Befehl[] {
  const b = V ? `/v/${encodeURIComponent(V)}` : null;
  const aus: Befehl[] = [];
  for (const v of VORLAGEN) {
    if (v.admin && !istAdmin(ich)) continue;
    const braucht = v.pfad !== undefined || v.recht !== undefined;
    if (braucht && (!b || !hatRecht(ich, V, v.recht ?? "lesen"))) continue;
    const e: Befehl = { id: v.id, titel: v.titel, ort: v.ort, icon: v.icon, stichworte: v.stichworte };
    if (v.pfad !== undefined) e.zu = b + (v.id === "pruefen" && !hatRecht(ich, V, "pruefen") ? "/lauf" : v.pfad);
    else if (v.zu) e.zu = v.zu;
    if (v.aktion) e.aktion = v.aktion;
    if (v.id === "thema") {
      e.titel = dunkel ? TX.shell.hell : TX.shell.dunkel;
      e.icon = dunkel ? "sun" : "moon";
    }
    aus.push(e);
  }
  // Verfahren wechseln: vor „Alle Verfahren“.
  const alle = aus.findIndex((e) => e.id === "verfahren-alle");
  const wechsel: Befehl[] = (ich?.verfahren ?? []).map((v) => ({
    id: "verfahren:" + v.code,
    titel: `${v.code} · ${v.titel}`,
    ort: v.code === V ? `${O.verfahren} · ${TX.befehle.aktuell}` : O.verfahren,
    icon: "arrow-right",
    stichworte: ["Verfahren wechseln", v.code],
    zu: `/v/${encodeURIComponent(v.code)}/auswerten`,
    verfahren: v.code,
  }));
  aus.splice(alle < 0 ? aus.length : alle, 0, ...wechsel);
  return aus;
}

/** Die Auswertung eines Verfahrens — ihr Zustand (Filter, Reiter, Dock) steht in der Suche (T15). */
const AUSWERTUNG = /^\/v\/[^/]+\/auswerten\/?$/;

/**
 * Wohin ein Eintrag mit Adresse führt, von der Adresse JETZT aus. Ein Reiter der Auswertung
 * (`?tab=`) auf der Auswertung, auf der man schon steht, tauscht nur den Reiter: Filter, Dock und
 * Auswahl bleiben — wie beim Klick auf den Reiter selbst (useAuswertungUrl `setzeUi`, ersetzt den
 * Verlaufseintrag). Gelesen und geschrieben über lib/rezept, nicht URLSearchParams: Listen stehen
 * dort kommagetrennt, ein Komma IM Wert ist kodiert — URLSearchParams verwischte beides (Review).
 */
export function zielVon(zu: string, jetzt: { pathname: string; search: string }): { zu: string; ersetzen: boolean } {
  const i = zu.indexOf("?");
  if (i < 0) return { zu, ersetzen: false };
  const weg = zu.slice(0, i);
  const ohneStrich = (p: string) => p.replace(/\/+$/, "");
  if (!AUSWERTUNG.test(weg) || ohneStrich(weg) !== ohneStrich(jetzt.pathname)) return { zu, ersetzen: false };
  const alt = leseAuswertung(jetzt.search);
  const s = baueAuswertung({ ...alt, ui: { ...alt.ui, tab: leseAuswertung(zu.slice(i)).ui.tab } });
  return { zu: jetzt.pathname + (s ? "?" + s : ""), ersetzen: true };
}

/* ------------------------------------------------------------------ Abgleich */

/**
 * Für den Vergleich: klein, ohne Akzente, ß → ss, und ae/oe/ue → a/o/u — so finden „prüfen“,
 * „pruefen“ und „prufen“ dasselbe (beide Seiten gehen durch dieselbe Mühle). Satzzeichen → Leerzeichen.
 */
export function normalisiere(s: string): string {
  return s
    .toLowerCase()
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/([aou])e/g, "$1")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

interface Vorbereitet {
  titel: string;
  titelWoerter: string[];
  ort: string;
  stichworte: string[];
}

function vorbereiten(b: Befehl): Vorbereitet {
  const titel = normalisiere(b.titel);
  return { titel, titelWoerter: titel.split(" "), ort: normalisiere(b.ort), stichworte: b.stichworte.map(normalisiere) };
}

const beginntWort = (text: string, t: string) => text.startsWith(t) || text.split(" ").some((w) => w.startsWith(t));

/** Ab so vielen Zeichen zählt ein einzelnes Suchwort auch mitten in einem Wort (Komposita). */
const TEILWORT_AB = 4;

/**
 * Gewicht EINES Suchworts: Wortanfang vor Teilwort, darin Titel vor Stichwort vor Ort; 0 = passt
 * nicht. Ein Teilwort („schlange“ → „Warte·schlange“) zählt nur mit `teilwort` und dann unter jedem
 * Wortanfang — sonst fände „orte laden“ die „Exp·orte“ (über „herunter·laden“) und „ort“ die
 * „Antw·ort·en“ vor den Stadtteilen (Review).
 */
function wortWert(p: Vorbereitet, t: string, teilwort: boolean): number {
  if (p.titel === t) return 100;
  if (p.titel.startsWith(t)) return 80;
  if (p.titelWoerter.some((w) => w.startsWith(t))) return 60;
  if (p.stichworte.some((s) => beginntWort(s, t))) return 30;
  if (beginntWort(p.ort, t)) return 15;
  if (!teilwort) return 0;
  if (p.titel.includes(t)) return 8;
  if (p.stichworte.some((s) => s.includes(t))) return 4;
  if (p.ort.includes(t)) return 2;
  return 0;
}

/**
 * Der Wert eines Eintrags für die ganze Suchzeile — 0, sobald ein Wort nirgends passt. Teilwörter
 * nur bei EINEM Suchwort ab TEILWORT_AB Zeichen; mehrere Wörter müssen je an einem Wortanfang passen.
 */
export function trefferWert(b: Befehl, suchzeile: string): number {
  const q = normalisiere(suchzeile);
  if (!q) return 1;
  const p = vorbereiten(b);
  const woerter = q.split(" ");
  const teilwort = woerter.length === 1 && q.length >= TEILWORT_AB;
  let summe = 0;
  for (const t of woerter) {
    const w = wortWert(p, t, teilwort);
    if (!w) return 0;
    summe += w;
  }
  // Die ganze Zeile als Anfang des Titels („bögen bil“) wiegt mehr als dieselben Wörter verstreut.
  if (p.titel.startsWith(q)) summe += 50;
  return summe;
}

/** Passende Einträge, die besten zuerst (Gleichstand: Reihenfolge des Verzeichnisses). Leere Zeile = alle. */
export function suchen(befehle: readonly Befehl[], suchzeile: string, hoechstens = 12): Befehl[] {
  if (!normalisiere(suchzeile)) return [...befehle];
  return befehle
    .map((b, i) => ({ b, i, w: trefferWert(b, suchzeile) }))
    .filter((x) => x.w > 0)
    .sort((x, y) => y.w - x.w || x.i - y.i)
    .slice(0, hoechstens)
    .map((x) => x.b);
}

/* ------------------------------------------------------------------ Codes */

const VC = "[A-Z]{2,5}\\d{2}";
/** Dieselbe Grammatik wie backend/app/codes.py (`ist_code`): Kennzahl, Nennung, Beleg, Bogen, Dokument, Menge, Export. */
const CODE_MUSTER: readonly RegExp[] = [
  /^X-\d{6}-\d{2,3}\.T\d{1,2}\.Z\d{2}\.S\d{2}$/,
  new RegExp(`^${VC}-\\d{3,4}-\\d{1,2}[a-z]{1,2}\\d?\\.\\d{1,2}$`),
  new RegExp(`^${VC}-\\d{3,4}-\\d{1,2}[a-z]{1,2}\\d?$`),
  new RegExp(`^${VC}-\\d{3,4}$`),
  new RegExp(`^${VC}-D\\d{2,3}$`),
  /^M-[a-z2-7]{8,10}$/,
  /^X-\d{6}-\d{2,3}$/,
];
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Sieht die Eingabe aus wie ein Code, den /r auflösen kann? Dann in der Schreibweise des Servers
 * (Verfahren groß, Beleg-Buchstaben klein, „-D03“, „M-…“, „X-…“) — sonst null. Kleingetipptes
 * („mu26-017-2c“) wird so zum Code statt zur Textsuche.
 */
export function alsCode(roh: string): string | null {
  const t = roh.trim();
  if (!t || /\s/.test(t)) return null;
  if (UUID.test(t.toLowerCase())) return t.toLowerCase();
  const passt = (k: string) => CODE_MUSTER.some((m) => m.test(k));
  if (passt(t)) return t;
  const v = /^([a-z]{2,5}\d{2})(-.+)$/i.exec(t);
  const k = v ? v[1]!.toUpperCase() + v[2]!.toLowerCase().replace(/^-d(\d)/, "-D$1") : /^m-/i.test(t) ? "M-" + t.slice(2).toLowerCase() : t.toUpperCase();
  return passt(k) ? k : null;
}

/* ------------------------------------------------------------------ Zeilen */

export type Zeile =
  | { art: "befehl"; befehl: Befehl }
  | { art: "code"; code: string }
  /** Freitext → die Fragebar der Auswertung, vorbelegt (gesperrt: der Grund) */
  | { art: "antworten"; text: string; gesperrt: string | null }
  /** Freitext → der KI-Assistent, gestellt (gesperrt: der Grund) */
  | { art: "ki"; text: string; gesperrt: string | null };

export const istGesperrt = (z: Zeile | undefined): boolean => !z || ((z.art === "antworten" || z.art === "ki") && z.gesperrt !== null);

/** Ab so vielen Zeichen bietet die Suche Text und KI an (wie die Fragebar). */
export const MIN_FREITEXT = 2;

/** Die Zeilen unter dem Suchfeld, in Anzeigereihenfolge: Seiten & Werkzeuge · Codes & Text · KI. */
export function zeilenFuer(befehle: readonly Befehl[], suchzeile: string, { V, ich }: Pick<BefehlKontext, "V" | "ich">): Zeile[] {
  const q = suchzeile.trim();
  const zeilen: Zeile[] = suchen(befehle, q).map((befehl) => ({ art: "befehl", befehl }));
  if (q.length < MIN_FREITEXT) return zeilen;
  const code = alsCode(q);
  if (code) zeilen.push({ art: "code", code });
  zeilen.push({ art: "antworten", text: q, gesperrt: !V ? TX.befehle.ohneVerfahren : hatRecht(ich, V, "lesen") ? null : TX.befehle.ohneRechtLesen });
  zeilen.push({ art: "ki", text: q, gesperrt: !V ? TX.befehle.ohneVerfahren : hatRecht(ich, V, "assistent") ? null : TX.befehle.ohneRechtKi });
  return zeilen;
}

/**
 * Welche Zeile ist vorgewählt? Ein Code; sonst „KI fragen“ bei „?“ oder ≥ 4 Wörtern (wie die
 * Fragebar); sonst der beste Eintrag; sonst die erste freie Zeile. -1 = nichts wählbar.
 */
export function vorwahl(suchzeile: string, zeilen: readonly Zeile[]): number {
  const code = zeilen.findIndex((z) => z.art === "code");
  if (code >= 0) return code;
  const woerter = suchzeile.trim().split(/\s+/).filter(Boolean);
  const ki = zeilen.findIndex((z) => z.art === "ki" && !istGesperrt(z));
  if (ki >= 0 && (suchzeile.includes("?") || woerter.length >= 4)) return ki;
  const befehl = zeilen.findIndex((z) => z.art === "befehl");
  if (befehl >= 0) return befehl;
  return zeilen.findIndex((z) => !istGesperrt(z));
}

/** Nächste wählbare Zeile in Richtung ±1, rundherum; gesperrte werden übersprungen. */
export function naechste(zeilen: readonly Zeile[], von: number, richtung: 1 | -1): number {
  const n = zeilen.length;
  if (!n) return -1;
  let i = von;
  for (let schritt = 0; schritt < n; schritt++) {
    i = i < 0 ? (richtung > 0 ? 0 : n - 1) : (i + richtung + n) % n;
    if (!istGesperrt(zeilen[i])) return i;
  }
  return -1;
}
